import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarcoPlantilla } from './MarcoPlantilla';
import { PLANTILLAS } from '../../../diseno/plantillas/catalog';
import { exportarJpeg } from '../../../utils/diseno/exportar';
import {
  CTAS,
  aplicarMarcaEnSlots,
  formatoCop,
  resolverMarca,
  slotsIniciales,
} from '../../../utils/diseno/marca';
import { combinacionesMarca, paletaDesdeColores } from '../../../utils/diseno/palette';
import { parseFormato } from '../../../utils/diseno/slots';
import type { AjusteImagen, BrandPalette, EmpresaMarcaInput, PlantillaFuente } from '../../../utils/diseno/types';
import { AJUSTE_IMAGEN_INICIAL } from '../../../utils/diseno/types';
import { ETIQUETA_ICONO } from '../../../utils/diseno/iconos';
import {
  guardarDisenoRedes,
  nuevoIdRedes,
  type DisenoRedes,
  type ImagenRedes,
} from '../../../stores/tusRedesStore';

export interface ProductoParaRedes {
  id: string;
  nombre: string;
  precioVenta: number;
  imagen?: string;
  imagenes?: string[];
}

export interface ImagenEstudio {
  id: string;
  nombre: string;
  imagen_url: string;
  tipo?: string;
}

interface Props {
  plantilla: PlantillaFuente;
  empresa: EmpresaMarcaInput & { id: string };
  productos: ProductoParaRedes[];
  imagenesEstudio: ImagenEstudio[];
  imagenesGuardadas: ImagenRedes[];
  fotoInicial?: string | null;
  inicial?: DisenoRedes | null;
  onClose: () => void;
  onGuardado: () => void;
}

function sufijo(nombre: string): string {
  const m = nombre.match(/_(\d+)$/);
  return m ? `_${m[1]}` : '';
}

function contarHashtags(texto: string): number {
  return (texto.match(/#[^\s#]+/g) || []).length;
}

export function TusRedesEditor({
  plantilla,
  empresa,
  productos,
  imagenesEstudio,
  imagenesGuardadas,
  fotoInicial,
  inicial,
  onClose,
  onGuardado,
}: Props) {
  const marca = useMemo(() => resolverMarca(empresa), [empresa]);
  const manifest = plantilla.manifest;
  const { width, height } = parseFormato(plantilla.formato);
  const [ancho, setAncho] = useState(320);
  const marcoRef = React.useRef<HTMLDivElement>(null);
  const docRef = React.useRef<Document | null>(null);

  const [slots, setSlots] = useState<Record<string, string>>(() => {
    const base = inicial?.slots || slotsIniciales(manifest, marca);
    if (inicial || !fotoInicial) return base;
    const destino =
      manifest.slots.find((s) => s.name === 'product_image')?.name ||
      manifest.slots.find((s) => s.type === 'image' && s.name !== 'logo')?.name;
    if (!destino) return base;
    return { ...base, [destino]: fotoInicial };
  });
  const [ajustes, setAjustes] = useState<Record<string, AjusteImagen>>(() => inicial?.ajustes || {});
  const paletaTemporada = manifest.paleta_temporada || null;
  const [palette, setPalette] = useState<BrandPalette | null>(
    () => (inicial ? inicial.palette : paletaTemporada || marca.palette)
  );
  const [usaMarca, setUsaMarca] = useState(inicial ? inicial.usaMarca : !paletaTemporada);
  const [caption, setCaption] = useState(inicial?.caption || '');
  const [altText, setAltText] = useState(inicial?.altText || slots.headline || manifest.slots.find((s) => s.name === 'headline')?.ejemplo || '');
  const [disenoId] = useState(inicial?.id || nuevoIdRedes());
  const [origenImagen, setOrigenImagen] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [activo, setActivo] = useState<string | null>(null);

  useEffect(() => {
    const el = marcoRef.current;
    if (!el) return;
    const medir = () => {
      const caja = el.getBoundingClientRect();
      const byWidth = Math.max(140, (caja.width || el.clientWidth) - 12);
      const altoCaja = caja.height > 80 ? caja.height - 16 : 0;
      const altoDisponible =
        window.innerWidth >= 1024
          ? window.innerHeight - 160
          : altoCaja || Math.min(window.innerHeight * 0.46, 420);
      const byHeight = altoDisponible * (width / height);
      setAncho(Math.max(140, Math.min(byWidth, byHeight)));
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, [width, height]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const aplicar = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : '';
    };
    aplicar();
    mq.addEventListener('change', aplicar);
    return () => {
      document.body.style.overflow = '';
      mq.removeEventListener('change', aplicar);
    };
  }, []);

  const enfocarSlot = (name: string) => {
    setActivo(name);
    window.setTimeout(() => {
      const el = document.getElementById(`slot-${name}`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const buscar = el?.querySelector('input[type="search"]');
      const input = buscar || el?.querySelector('input:not([type="file"]), textarea, select');
      if (input instanceof HTMLElement) input.focus({ preventScroll: true });
    }, 40);
  };

  const moverImagen = (name: string, x: number, y: number) => {
    setActivo(name);
    setAjustes((prev) => ({
      ...prev,
      [name]: { ...(prev[name] || AJUSTE_IMAGEN_INICIAL), x, y },
    }));
  };

  const zoomImagen = (name: string, zoom: number) => {
    setAjustes((prev) => ({
      ...prev,
      [name]: { ...(prev[name] || AJUSTE_IMAGEN_INICIAL), zoom },
    }));
  };

  const combinaciones = useMemo(
    () => combinacionesMarca(empresa.configuracion?.branding?.colores),
    [empresa]
  );

  const usarMarca = () => {
    setPalette(marca.palette);
    setSlots((prev) => aplicarMarcaEnSlots(prev, marca));
    setUsaMarca(true);
  };

  const setTexto = (name: string, value: string) => {
    setSlots((prev) => ({ ...prev, [name]: value }));
    if (name === 'headline') setAltText((alt) => (alt === slots.headline ? value : alt));
  };

  const setImagen = (name: string, src: string) => {
    setSlots((prev) => ({ ...prev, [name]: src }));
    setAjustes((prev) => ({ ...prev, [name]: prev[name] || { ...AJUSTE_IMAGEN_INICIAL } }));
    setOrigenImagen(null);
  };

  const elegirProducto = (slot: string, producto: ProductoParaRedes) => {
    const foto = producto.imagenes?.[0] || producto.imagen || '';
    if (!foto) {
      setAviso('Ese producto no tiene foto.');
      return;
    }
    const extra = sufijo(slot);
    setSlots((prev) => {
      const next = { ...prev, [slot]: foto };
      const nombreSlot = `product_name${extra}`;
      const precioSlot = `price${extra}`;
      if (nombreSlot in next) next[nombreSlot] = producto.nombre;
      if (precioSlot in next) next[precioSlot] = formatoCop(producto.precioVenta);
      if (slot === 'product_image' && 'headline' in next) next.headline = producto.nombre;
      return next;
    });
    setAjustes((prev) => ({ ...prev, [slot]: prev[slot] || { ...AJUSTE_IMAGEN_INICIAL } }));
    setOrigenImagen(null);
    setAviso('Llenamos la foto y el texto del producto. Puedes cambiarlo.');
  };

  const elegirCta = (texto: string, contacto: string) => {
    setSlots((prev) => {
      const next = { ...prev, cta: texto };
      if ('contact' in next && contacto) next.contact = contacto;
      return next;
    });
  };

  const armarDiseno = (estado: DisenoRedes['estado']): DisenoRedes => ({
    id: disenoId,
    empresaId: empresa.id,
    plantillaId: plantilla.id,
    formato: plantilla.formato,
    nombre: manifest.nombre,
    slots,
    ajustes,
    palette,
    usaMarca,
    caption,
    altText,
    estado,
    updatedAt: new Date().toISOString(),
  });

  const descargar = async () => {
    const doc = docRef.current;
    if (!doc?.body) return;
    setOcupado(true);
    setAviso(null);
    try {
      const blob = await exportarJpeg(doc, width, height);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plantilla.id}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      await guardarDisenoRedes(armarDiseno('descargada'));
      onGuardado();
      setAviso('Listo. Publica la imagen desde Instagram o WhatsApp.');
    } catch {
      setAviso('No pudimos armar el JPEG. Intenta de nuevo.');
    } finally {
      setOcupado(false);
    }
  };

  const compartir = async () => {
    const doc = docRef.current;
    if (!doc?.body) return;
    setOcupado(true);
    try {
      const blob = await exportarJpeg(doc, width, height);
      const file = new File([blob], `${plantilla.id}.jpg`, { type: 'image/jpeg' });
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({ files: [file], text: caption, title: manifest.nombre });
      } else {
        setAviso('Este navegador no abre la hoja de compartir. Usa Descargar.');
      }
      await guardarDisenoRedes(armarDiseno('descargada'));
      onGuardado();
    } catch (e) {
      if ((e as { name?: string })?.name !== 'AbortError') {
        setAviso('No pudimos compartir. Descarga la imagen y publícala tú.');
      }
    } finally {
      setOcupado(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setAviso('Texto copiado.');
    } catch {
      setAviso('No pudimos copiar el texto.');
    }
  };

  const guardarBorrador = async () => {
    await guardarDisenoRedes(armarDiseno('borrador'));
    onGuardado();
    setAviso('Borrador guardado en este dispositivo.');
  };

  const textoSlots = manifest.slots.filter((s) => s.type === 'text');
  const iconSlots = manifest.slots.filter((s) => s.type === 'icon');
  const imagenSlots = manifest.slots.filter((s) => s.type === 'image');
  const coloresActuales = palette || marca.palette;
  const eligiendoFoto = Boolean(origenImagen);

  const ajustarZoom = (delta: number) => {
    const conFoto =
      imagenSlots.find((s) => s.name === activo && slots[s.name]) ||
      imagenSlots.find((s) => (s.name === 'product_image' || s.name === 'background_image') && slots[s.name]) ||
      imagenSlots.find((s) => s.name !== 'logo' && slots[s.name]) ||
      imagenSlots.find((s) => slots[s.name]);
    const name = conFoto?.name;
    if (!name) return;
    const actual = ajustes[name]?.zoom || 1;
    zoomImagen(name, Math.max(1, Math.min(2.5, Math.round((actual + delta) * 20) / 20)));
  };

  return (
    <div
      className="max-lg:fixed max-lg:inset-0 max-lg:z-30 max-lg:flex max-lg:flex-col max-lg:overflow-hidden max-lg:bg-white max-lg:pb-[env(safe-area-inset-bottom)] lg:space-y-4"
      data-testid="tus-redes-editor"
    >
      <div className="flex items-center justify-between gap-2 shrink-0 max-lg:px-3 max-lg:pt-[max(0.75rem,env(safe-area-inset-top))]">
        <button type="button" onClick={onClose} className="text-sm text-blue-700 hover:underline min-h-[44px]">
          ← Plantillas
        </button>
        <h2 className="text-lg font-semibold text-[#0B1F4A]">{manifest.nombre}</h2>
      </div>

      {(marca.sinLogo || marca.sinColores) && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 shrink-0 max-lg:mx-3">
          Agrega tu logo y colores en Configuración para que tus diseños salgan con tu marca.{' '}
          <Link to="/configuracion?tab=branding" className="underline font-medium">
            Abrir Configuración
          </Link>
        </p>
      )}

      <div className={`min-h-0 max-lg:flex-1 max-lg:flex max-lg:flex-col max-lg:px-3 lg:grid lg:grid-cols-[minmax(260px,460px)_minmax(0,1fr)] lg:gap-6`}>
      <div className="max-lg:flex-1 max-lg:min-h-0 max-lg:flex max-lg:flex-col lg:block">
      <div className="lg:sticky lg:top-3 bg-white max-lg:flex-1 max-lg:min-h-0 max-lg:flex max-lg:flex-col lg:pb-2">
      <p className="text-xs text-gray-600 mb-2 px-1 shrink-0">
        <span className="lg:hidden">Toca un texto para editarlo. Arrastra la foto con el dedo. Pellizca o usa + y − para el zoom.</span>
        <span className="hidden lg:inline">Toca un texto para editarlo. Arrastra la foto para acomodarla. La rueda del mouse hace zoom.</span>
      </p>
      <div ref={marcoRef} className="relative w-full max-lg:flex-1 max-lg:min-h-0 flex items-center justify-center bg-gray-100 rounded-lg p-2">
        <MarcoPlantilla
          html={plantilla.html}
          formato={plantilla.formato}
          slots={slots}
          palette={palette}
          ajustes={ajustes}
          ancho={ancho}
          titulo={manifest.nombre}
          interactivo
          activo={activo}
          onSeleccionar={enfocarSlot}
          onMoverImagen={moverImagen}
          onZoomImagen={zoomImagen}
          onDocumento={(doc) => {
            docRef.current = doc;
          }}
        />
        <div className="absolute bottom-3 right-3 flex gap-2 lg:hidden">
          <button type="button" aria-label="Alejar foto" onClick={() => ajustarZoom(-0.15)} className="h-11 w-11 rounded-full bg-white text-xl font-semibold text-[#0B1F4A] shadow">
            −
          </button>
          <button type="button" aria-label="Acercar foto" onClick={() => ajustarZoom(0.15)} className="h-11 w-11 rounded-full bg-white text-xl font-semibold text-[#0B1F4A] shadow">
            +
          </button>
        </div>
      </div>
      </div>
      </div>
      <div className={`space-y-4 min-w-0 max-lg:min-h-0 max-lg:overflow-y-auto max-lg:px-3 max-lg:pb-4 ${eligiendoFoto ? 'max-lg:max-h-[70%]' : 'max-lg:max-h-[46%]'}`}>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={usarMarca}
          className="min-h-[44px] px-4 rounded-full text-sm font-semibold text-white bg-[#1A56DB]"
        >
          Usar mi marca
        </button>
        {paletaTemporada && (
          <button
            type="button"
            onClick={() => {
              setPalette(paletaTemporada);
              setUsaMarca(false);
            }}
            className="min-h-[44px] px-3 rounded-full text-sm border border-gray-300 bg-white"
          >
            Colores de temporada
          </button>
        )}
        {!coloresActuales && (
          <button
            type="button"
            onClick={() => {
              setPalette(paletaDesdeColores({ primario: '#111827', secundario: '#1E3A8A', acento: '#D97706' }, true));
              setUsaMarca(false);
            }}
            className="min-h-[44px] px-3 rounded-full text-sm border border-gray-300 bg-white"
          >
            Elegir colores
          </button>
        )}
        {combinaciones.slice(1).map((combo, i) => (
          <button
            key={`${combo['--brand-primary']}-${i}`}
            type="button"
            onClick={() => {
              setPalette(combo);
              setUsaMarca(false);
            }}
            className="min-h-[44px] px-3 rounded-full text-sm border border-gray-300 bg-white"
          >
            Combinación {i + 1}
          </button>
        ))}
      </div>

      {coloresActuales && (
        <div className="grid grid-cols-3 gap-3">
          {(
            [
              ['--brand-primary', 'Primario'],
              ['--brand-secondary', 'Secundario'],
              ['--brand-accent', 'Acento'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="text-xs text-gray-600">
              {label}
              <input
                type="color"
                className="mt-1 block w-full h-11 border border-gray-300 rounded-md"
                value={coloresActuales[key]}
                onChange={(e) => {
                  const primario = key === '--brand-primary' ? e.target.value : coloresActuales['--brand-primary'];
                  const secundario = key === '--brand-secondary' ? e.target.value : coloresActuales['--brand-secondary'];
                  const acento = key === '--brand-accent' ? e.target.value : coloresActuales['--brand-accent'];
                  setPalette(paletaDesdeColores({ primario, secundario, acento }, true));
                  setUsaMarca(false);
                }}
              />
            </label>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {iconSlots.map((slot) => (
          <label key={slot.name} id={`slot-${slot.name}`} className={`block text-sm text-gray-700 rounded-lg ${activo === slot.name ? 'ring-2 ring-[#1A56DB] p-1' : ''}`}>
            {etiquetaSlot(slot.name)}
            <select
              value={slots[slot.name] || slot.ejemplo || ''}
              onFocus={() => setActivo(slot.name)}
              onChange={(e) => setTexto(slot.name, e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[44px] bg-white"
            >
              {(slot.opciones || []).map((op) => (
                <option key={op} value={op}>
                  {ETIQUETA_ICONO[op] || op}
                </option>
              ))}
            </select>
          </label>
        ))}
        {textoSlots.map((slot) => (
          <label key={slot.name} id={`slot-${slot.name}`} className={`block rounded-lg ${activo === slot.name ? 'ring-2 ring-[#1A56DB] p-1' : ''}`}>
            <span className="flex justify-between text-sm text-gray-700">
              <span>{etiquetaSlot(slot.name)}</span>
              {slot.max_chars ? (
                <span className={(slots[slot.name] || '').length > slot.max_chars ? 'text-red-600' : 'text-gray-400'}>
                  {(slots[slot.name] || '').length}/{slot.max_chars}
                </span>
              ) : null}
            </span>
            {slot.name === 'cta' && (
              <span className="flex flex-wrap gap-2 my-2">
                {CTAS.map((cta) => (
                  <button
                    key={cta.id}
                    type="button"
                    onClick={() => elegirCta(cta.texto, marca.contactos[cta.contacto])}
                    className="min-h-[44px] px-3 rounded-full text-xs border border-gray-300 bg-white"
                  >
                    {cta.texto}
                  </button>
                ))}
              </span>
            )}
            <input
              value={slots[slot.name] || ''}
              maxLength={slot.max_chars ? slot.max_chars + 20 : undefined}
              onFocus={() => setActivo(slot.name)}
              onChange={(e) => setTexto(slot.name, e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </label>
        ))}
      </div>

      <div className="space-y-4">
        {imagenSlots.map((slot) => (
          <div key={slot.name} id={`slot-${slot.name}`} className={`border rounded-lg p-3 space-y-2 ${activo === slot.name ? 'border-[#1A56DB] ring-2 ring-[#1A56DB]' : 'border-gray-200'}`}>
            <p className="text-sm font-medium text-gray-800">{etiquetaSlot(slot.name)}</p>
            <div className="flex flex-wrap gap-2">
              <label className="min-h-[44px] px-3 inline-flex items-center rounded-full text-sm border border-gray-300 bg-white cursor-pointer">
                Subir desde tu dispositivo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setImagen(slot.name, String(reader.result || ''));
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              <button
                type="button"
                className="min-h-[44px] px-3 rounded-full text-sm border border-gray-300 bg-white"
                onClick={() => {
                  setBusqueda('');
                  const next = origenImagen === `producto:${slot.name}` ? null : `producto:${slot.name}`;
                  setOrigenImagen(next);
                  if (next) enfocarSlot(slot.name);
                }}
              >
                Desde tus productos
              </button>
              <button
                type="button"
                className="min-h-[44px] px-3 rounded-full text-sm border border-gray-300 bg-white"
                onClick={() => {
                  setBusqueda('');
                  const next = origenImagen === `estudio:${slot.name}` ? null : `estudio:${slot.name}`;
                  setOrigenImagen(next);
                  if (next) enfocarSlot(slot.name);
                }}
              >
                Biblioteca del Centro de Diseño
              </button>
            </div>
            {slots[slot.name] && (
              <label className="block text-xs text-gray-600">
                Zoom
                <input
                  type="range"
                  min={1}
                  max={2.5}
                  step={0.05}
                  value={ajustes[slot.name]?.zoom || 1}
                  onChange={(e) => zoomImagen(slot.name, Number(e.target.value))}
                  className="w-full"
                />
              </label>
            )}
            {origenImagen === `producto:${slot.name}` && (
              <SelectorVisual
                placeholder="Buscar producto por nombre"
                busqueda={busqueda}
                onBusqueda={setBusqueda}
                hayFuente={productos.some((p) => fotoProducto(p))}
                vacio="No hay productos con foto."
                sinCoincidencia="Ningún producto coincide con esa búsqueda."
                items={productos
                  .filter((p) => fotoProducto(p))
                  .filter((p) => coincide(p.nombre, busqueda))
                  .map((p) => ({
                    id: p.id,
                    nombre: p.nombre,
                    detalle: formatoCop(p.precioVenta),
                    src: fotoProducto(p),
                    onElegir: () => elegirProducto(slot.name, p),
                  }))}
              />
            )}
            {origenImagen === `estudio:${slot.name}` && (
              <SelectorVisual
                placeholder="Buscar en la biblioteca"
                busqueda={busqueda}
                onBusqueda={setBusqueda}
                hayFuente={imagenesEstudio.length + imagenesGuardadas.length > 0}
                vacio="Todavía no hay fotos en la biblioteca. Súbelas en Imágenes → Modelos y fondos, o guárdalas al editar una imagen."
                sinCoincidencia="Ninguna imagen coincide con esa búsqueda."
                items={[
                  ...imagenesEstudio.map((img) => ({
                    id: img.id,
                    nombre: img.nombre,
                    detalle: etiquetaTipoImagen(img.tipo),
                    src: img.imagen_url,
                    onElegir: () => setImagen(slot.name, img.imagen_url),
                  })),
                  ...imagenesGuardadas.map((img) => ({
                    id: img.id,
                    nombre: img.nombre,
                    detalle: 'Guardada',
                    src: img.dataUrl,
                    onElegir: () => setImagen(slot.name, img.dataUrl),
                  })),
                ].filter((item) => coincide(`${item.nombre} ${item.detalle}`, busqueda))}
              />
            )}
          </div>
        ))}
      </div>

      <label className="block">
        <span className="flex justify-between text-sm text-gray-700">
          <span>Texto de la publicación</span>
          <span className="text-gray-400">
            {caption.length}/2200 · {contarHashtags(caption)} hashtags
          </span>
        </span>
        <textarea
          value={caption}
          maxLength={2200}
          rows={4}
          onChange={(e) => setCaption(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="Escribe el texto que vas a pegar en Instagram"
        />
      </label>
      <label className="block text-sm text-gray-700">
        Texto alternativo
        <input
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </label>

      {aviso && <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">{aviso}</p>}

      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={ocupado} onClick={() => void descargar()} className="min-h-[44px] px-4 rounded-full text-sm font-semibold text-white bg-[#0B1F4A] disabled:opacity-50">
          {ocupado ? 'Preparando…' : 'Descargar'}
        </button>
        <button type="button" disabled={ocupado} onClick={() => void compartir()} className="min-h-[44px] px-4 rounded-full text-sm font-semibold border border-[#1A56DB] text-[#1A56DB] disabled:opacity-50">
          Compartir
        </button>
        <button type="button" onClick={() => void copiar()} className="min-h-[44px] px-4 rounded-full text-sm border border-gray-300 bg-white">
          Copiar texto
        </button>
        <button type="button" onClick={() => void guardarBorrador()} className="min-h-[44px] px-4 rounded-full text-sm border border-gray-300 bg-white">
          Guardar borrador
        </button>
      </div>
      <p className="text-xs text-gray-500">La publicación directa en Instagram llega en una siguiente etapa. Por ahora descargas y publicas tú.</p>
      </div>
      </div>
    </div>
  );
}

export function plantillaPorId(id: string, formato: string): PlantillaFuente | undefined {
  return PLANTILLAS.find((p) => p.id === id && p.formato === formato);
}

const ETIQUETAS: Record<string, string> = {
  logo: 'Logo',
  business_name: 'Nombre del negocio',
  eyebrow: 'Etiqueta',
  headline: 'Título',
  subheadline: 'Subtítulo',
  body: 'Texto',
  price: 'Precio',
  price_label: 'Etiqueta de precio',
  price_before: 'Precio anterior',
  discount_badge: 'Descuento',
  cta: 'Llamado a la acción',
  contact: 'Contacto',
  date_or_hours: 'Fecha u horario',
  product_image: 'Foto del producto',
  background_image: 'Foto de fondo',
  product_name: 'Nombre del producto',
};

function textoBusqueda(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es');
}

function coincide(texto: string, consulta: string): boolean {
  const q = textoBusqueda(consulta.trim());
  if (!q) return true;
  return textoBusqueda(texto).includes(q);
}

function fotoProducto(producto: ProductoParaRedes): string {
  return producto.imagenes?.[0] || producto.imagen || '';
}

function etiquetaTipoImagen(tipo?: string): string {
  if (tipo === 'modelo') return 'Modelo';
  if (tipo === 'fondo') return 'Fondo';
  if (tipo === 'recorte') return 'Recorte';
  return 'Biblioteca';
}

function SelectorVisual({
  placeholder,
  busqueda,
  onBusqueda,
  hayFuente,
  vacio,
  sinCoincidencia,
  items,
}: {
  placeholder: string;
  busqueda: string;
  onBusqueda: (value: string) => void;
  hayFuente: boolean;
  vacio: string;
  sinCoincidencia: string;
  items: { id: string; nombre: string; detalle: string; src: string; onElegir: () => void }[];
}) {
  return (
    <div className="space-y-2">
      <input
        type="search"
        value={busqueda}
        onChange={(e) => onBusqueda(e.target.value)}
        placeholder={placeholder}
        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[44px]"
      />
      {!hayFuente && <p className="text-sm text-gray-500">{vacio}</p>}
      {hayFuente && items.length === 0 && <p className="text-sm text-gray-500">{sinCoincidencia}</p>}
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-80 overflow-auto">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={item.onElegir}
              className="text-left border border-gray-200 rounded-lg overflow-hidden bg-white hover:border-[#1A56DB]"
            >
              <img src={item.src} alt="" className="w-full h-24 object-cover bg-gray-100" />
              <span className="block px-2 py-1.5">
                <span className="block text-xs font-medium text-gray-800 line-clamp-2">{item.nombre}</span>
                <span className="block text-[11px] text-gray-500">{item.detalle}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function etiquetaSlot(name: string): string {
  const medio = name.match(/^method_(\d+)(?:_(icon|detail))?$/);
  if (medio) {
    const n = medio[1];
    if (medio[2] === 'icon') return `Icono ${n}`;
    if (medio[2] === 'detail') return `Detalle ${n}`;
    return `Medio de pago ${n}`;
  }
  const directa = ETIQUETAS[name];
  if (directa) return directa;
  const m = name.match(/^(.*)_(\d+)$/);
  const base = m?.[1];
  const indice = m?.[2];
  if (base && indice && ETIQUETAS[base]) return `${ETIQUETAS[base]} ${indice}`;
  return name.replace(/_/g, ' ');
}
